import * as React from 'react';
import * as _ from 'underscore';

import { CloseButtonWidget } from '@tableau/widgets';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { AngularDirective } from '../../common/react/components/AngularDirective';
import { toNativePromise } from '../../commonInfra/utils/QUtils';
import { Views } from '../../views/resources/Views';
import { IVizScope } from '../../ts/directives/tbViz';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { Order } from '../../ts/helpers/Order';
import { DeviceType } from '../../ts/models/DeviceType';
import { IViewDetailInfo, IViewInfo } from '../../ts/models/IViewInfo';
import { VizType } from '../../ts/models/Viz';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { DevicePreviewService } from '../../devicePreview/services/DevicePreviewService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { VizService } from '../../ts/services/VizService';
import { DevicePickerBar } from './DevicePickerBar';

export interface DevicePreviewModalProps {
  readonly angularContext: AngularContext;
  readonly vizPath: string;
  readonly dismiss: () => void;
}

interface DevicePreviewModalState {
  readonly devicePickerBarProps: DevicePickerBar.Props;
  readonly vizService: VizService;
  readonly sessionManagementService: SessionManagementService;
}

export class DevicePreviewModal extends React.PureComponent<DevicePreviewModalProps, DevicePreviewModalState> {

  public constructor(props: DevicePreviewModalProps) {
    super(props);
    this.state = {
      vizService: ServiceLocator.get<VizService>('VizService'),
      sessionManagementService: getSessionManagementServiceInstance(),
      devicePickerBarProps: {
        onDeviceTypeClick: (device: DeviceType) => {
          const previewDevice: DeviceType = DevicePreviewService.changePreviewDevice(device);
          if (previewDevice) {
            this.handleDeviceChange(previewDevice);
          }
        },
        selectedDevice: DevicePreviewService.getPreviewDevice(),
      }
    };
  }

  public componentDidMount() {
    this.initializeNavigationToolsProps();
  }

  private getViewCountWrapper(viewsQuery: ResourceQuery<IViewInfo>) {
    return toNativePromise(viewsQuery.getCount());
  }

  private getViewSliceWrapper(viewsQuery: ResourceQuery<IViewInfo>, start: number, size: number) {
    return toNativePromise(viewsQuery.getSlice(start, size));
  }

  private initializeNavigationToolsProps() {
    Views.getByPath(this.props.vizPath)
    .then((viewInfo: IViewDetailInfo) => {
      const orderClause = [ Order.asc('index') ];
      const viewsQuery = Views.forWorkbook(viewInfo.workbook).withOrder(orderClause);

      const viewsForWorkbook = this.getViewCountWrapper(viewsQuery)
      .then((viewCount) => this.getViewSliceWrapper(viewsQuery, 0, viewCount))
      .then((result) => result.items);

      return Promise.all([
        viewsForWorkbook,
        viewInfo.id
      ]);
    })
    .then((values: any[]) => {
      const viewsForWorkbook = values[0];
      const currentViewId = values[1];
      this.setState({
        devicePickerBarProps: _.extend({}, this.state.devicePickerBarProps, {
          navigationToolsProps: this.buildNavigationToolsProps(viewsForWorkbook, currentViewId)
        })
      });
    });
  }

  // public for testing purposes only
  public buildNavigationToolsProps(viewList: IViewInfo[], currentViewId: string) {
    const indexOfCurrentView = viewList.findIndex(view => view.id === currentViewId);

    return {
      viewList: viewList,
      startingViewIndex: (indexOfCurrentView !== -1 ? indexOfCurrentView : 0),
      onNewViewSelected: (newView: IViewInfo) => {
        this.handleChangedPath(newView.path);
      }
    };
  }

  private handleDeviceChange(previewDevice: DeviceType) {
    const scope = this.props.angularContext.scope as IVizScope;
    this.setState({
      devicePickerBarProps: _.extend({}, this.state.devicePickerBarProps, {
        selectedDevice: previewDevice
      })
    }, scope.viz.reloadViz);
  }

  private handleChangedPath(newPath: string) {
    const scope = this.props.angularContext.scope as IVizScope;
    scope.$apply(() =>
      scope.viz.path = newPath
    );
    scope.viz.reloadViz();
  }

  private readonly setupVizScope = (scope: ng.IScope): IVizScope => {
    const vizScope = scope as IVizScope;
    vizScope.viz = {
      path: this.props.vizPath,
      vizType: VizType.DeviceVizPreview,
      params: this.state.vizService.getVizParams(),
      siteUrlName: this.state.sessionManagementService.sessionSiteUrlName(),
      instanceId: this.state.vizService.getInstanceId(),
      isExplicitOriginalViz: this.state.vizService.getIsExplicitOriginalViz(),
      allowReloadViz: true
    };

    vizScope.$watch('viz.isExplicitOriginalViz', (isExplicitOriginalViz: boolean) => {
      this.state.vizService.updateExplicitOriginalViz(vizScope, isExplicitOriginalViz);
    });
    return vizScope;
  }

  private closeButton(): JSX.Element {
    // TFS 840752: In IE11, it is necessary to supply a "height" style to the button in order to
    // have the flex CSS center the icon vertically. It actually doesn't matter what the "height"
    // value actually is, so long as it is smaller than the "min-height" (which is automatically
    // supplied by ButtonWidget).
    // See this for more info: https://stackoverflow.com/a/25386261
    const buttonStyle: React.CSSProperties = {
      float: 'right',
      cursor: 'pointer',
      height: 0
    };
    const testId = 'device-preview-close';
    const buttonWidgetProps = {
      handleClick: this.props.dismiss,
      testId: testId,
      style: buttonStyle,
      useDarkStyling: true,
    };

    return (
      <CloseButtonWidget {...buttonWidgetProps}/>
    );
  }

  private title(): JSX.Element {
    return (
      <div className="tb-preview-title-box">
        <span>{Messages.previewDeviceLayouts()}</span>
      </div>
    );
  }

  public render(): JSX.Element {
    const template = `<tb:viz
      class="tb-device-viz-preview ng-isolate-scope"
      viz="viz",
      data-tb-test-id="preview-viz"></tb:viz>`;

    return (
    <React.Fragment>
      {this.closeButton()}
      {this.title()}
      <div className="tb-device-picker-bar">
        <DevicePickerBar {...this.state.devicePickerBarProps} />
      </div>
      <div className="tb-fill tb-device-viz-preview-fill">
        <AngularDirective
          setupScope={this.setupVizScope}
          angularContext={this.props.angularContext}
          template={template}
        />
      </div>
    </React.Fragment>);
  }
}
